import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Empty, Row, Segmented, Spin, Statistic, Table, Tabs, Tag, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { get } from '../../api/request';
import './AnalyticsPage.css';

type TrendItem = { date: string; active_users: number; effective_users: number; events: number };
type Overview = {
  summary: Record<string, number>;
  trend: TrendItem[];
  top_events: { event_name: string; count: number }[];
  top_pages: { page_id: string; users: number }[];
};

const labels: Record<string, string> = {
  radio: '电台', generate: '生成', schedule: '日程', me: '我的',
  audio_detail: '音频详情', voice_preference: '声音偏好', login: '登录', register: '注册',
};

const TrendChart: React.FC<{ data: TrendItem[] }> = ({ data }) => {
  if (!data.some(item => item.active_users || item.effective_users)) return <Empty description="所选时段暂无活跃数据" />;
  const width = 760, height = 220, pad = 30;
  const max = Math.max(1, ...data.flatMap(item => [item.active_users, item.effective_users]));
  const points = (key: 'active_users' | 'effective_users') => data.map((item, index) => {
    const x = pad + index * ((width - pad * 2) / Math.max(data.length - 1, 1));
    const y = height - pad - item[key] / max * (height - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="analytics-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="活跃用户趋势">
        {[0, .25, .5, .75, 1].map(ratio => <line key={ratio} x1={pad} x2={width-pad} y1={pad+(height-pad*2)*ratio} y2={pad+(height-pad*2)*ratio} className="grid-line" />)}
        <polyline points={points('active_users')} className="active-line" />
        <polyline points={points('effective_users')} className="effective-line" />
      </svg>
      <div className="chart-legend"><span className="active-dot" />活跃用户 <span className="effective-dot" />有效陪伴用户</div>
      <div className="chart-axis"><span>{data[0]?.date.slice(5)}</span><span>{data[data.length - 1]?.date.slice(5)}</span></div>
    </div>
  );
};

const AnalyticsPage: React.FC = () => {
  const [days, setDays] = useState(7);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [funnel, setFunnel] = useState<any[]>([]);
  const [retention, setRetention] = useState<any[]>([]);

  useEffect(() => {
    setLoading(true);
    const params = range
      ? `start_date=${range[0].format('YYYY-MM-DD')}&end_date=${range[1].format('YYYY-MM-DD')}`
      : `days=${days}`;
    Promise.all([
      get<Overview>(`/analytics/overview?${params}`),
      get<{ steps: any[] }>(`/analytics/funnel?days=${days}`),
      get<{ points: any[] }>('/analytics/retention?cohort_days=30'),
    ]).then(([overviewData, funnelData, retentionData]) => {
      setOverview(overviewData);
      setFunnel(funnelData.steps);
      setRetention(retentionData.points);
    }).catch(error => message.error(error.message || '行为数据加载失败')).finally(() => setLoading(false));
  }, [days, range]);

  const cards = useMemo(() => {
    const s = overview?.summary || {};
    return [
      ['活跃用户', s.active_users || 0, '人'],
      ['有效陪伴用户', s.effective_users || 0, '人'],
      ['有效陪伴率', s.effective_rate || 0, '%'],
      ['行为事件', s.events || 0, '次'],
      ['会话数', s.sessions || 0, '次'],
      ['平均会话时长', s.avg_session_duration_sec || 0, '秒'],
    ];
  }, [overview]);

  return (
    <div className="analytics-page">
      <div className="analytics-heading">
        <div><h1>用户行为分析</h1><p>从访问、内容消费到有效陪伴，查看用户真实使用路径</p></div>
        <div className="analytics-filters">
          <Segmented value={days} options={[{label:'近7天',value:7},{label:'近30天',value:30},{label:'近90天',value:90}]} onChange={value => { setRange(null); setDays(Number(value)); }} />
          <DatePicker.RangePicker value={range} allowClear onChange={value => setRange(value as [Dayjs, Dayjs] | null)} disabledDate={current => current && current > dayjs().endOf('day')} />
        </div>
      </div>
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>{cards.map(([title, value, suffix]) => <Col xs={12} lg={8} xl={4} key={String(title)}><Card className="metric-card"><Statistic title={title} value={value} suffix={suffix} /></Card></Col>)}</Row>
        <Card title="活跃与有效陪伴趋势" className="analytics-block"><TrendChart data={overview?.trend || []} /></Card>
        <Tabs className="analytics-block" items={[
          { key: 'events', label: '热门事件', children: <Table size="small" pagination={false} rowKey="event_name" dataSource={overview?.top_events || []} columns={[{title:'事件',dataIndex:'event_name',render:value=><Tag color="blue">{value}</Tag>},{title:'触发次数',dataIndex:'count',align:'right'}]} /> },
          { key: 'pages', label: '页面访问', children: <Table size="small" pagination={false} rowKey="page_id" dataSource={overview?.top_pages || []} columns={[{title:'页面',dataIndex:'page_id',render:value=>labels[value] || value},{title:'访问用户',dataIndex:'users',align:'right'}]} /> },
          { key: 'funnel', label: '核心漏斗', children: <div className="funnel">{funnel.map((item, index) => <div className="funnel-step" key={item.label} style={{width:`${Math.max(item.conversion_rate, 18)}%`}}><strong>{index+1}. {item.label}</strong><span>{item.users} 人 · {item.conversion_rate}%</span></div>)}</div> },
          { key: 'retention', label: '新增留存', children: <Table size="small" pagination={false} rowKey="day" dataSource={retention} columns={[{title:'留存周期',dataIndex:'day',render:value=>`第 ${value} 天`},{title:'可观察新增用户',dataIndex:'cohort_users'},{title:'留存用户',dataIndex:'retained_users'},{title:'留存率',dataIndex:'rate',render:value=>`${value}%`}]} /> },
        ]} />
      </Spin>
    </div>
  );
};

export default AnalyticsPage;
